import { defineStore } from 'pinia';
import type { Schemas } from '#shopware';
import type { BatchProductSearchResult } from '~/composables/shopware/useBatchProductSearch';

type ProductDataMap = Record<string, Schemas['ProductDetailResponse']>;

export const useCartProductsStore = defineStore('cartProducts', {
    state: () => ({
        products: {} as ProductDataMap,
        isLoading: false,
        fetchPromise: null as Promise<void> | null,
        lastFetchedIds: '' as string,
    }),

    actions: {
        async fetchProductsWithSearchFn(
            cartItems: Schemas['LineItem'][],
            searchFn: (ids: string[]) => Promise<BatchProductSearchResult>,
        ) {
            const productIds = cartItems
                .map(item => item.referencedId)
                .filter((id): id is string => !!id);

            if (productIds.length === 0) {
                this.products = {};
                this.lastFetchedIds = '';
                return;
            }

            const idsKey = [...productIds].sort().join(',');

            // Skip if we already have the same products
            if (idsKey === this.lastFetchedIds && Object.keys(this.products).length > 0) {
                return;
            }

            // If already fetching, wait for that request
            if (this.fetchPromise) {
                await this.fetchPromise;
                return;
            }

            this.isLoading = true;

            const doFetch = async () => {
                try {
                    const result = await searchFn(productIds);
                    const productObj: ProductDataMap = {};
                    result.forEach((value, key) => {
                        productObj[key] = value;
                    });
                    this.products = productObj;
                    this.lastFetchedIds = idsKey;
                } catch (error) {
                    console.error('Failed to fetch cart products:', error);
                } finally {
                    this.isLoading = false;
                    this.fetchPromise = null;
                }
            };

            this.fetchPromise = doFetch();
            await this.fetchPromise;
        },

        getProduct(productId: string): Schemas['ProductDetailResponse'] | undefined {
            return this.products[productId];
        },

        clearProducts() {
            this.products = {};
            this.lastFetchedIds = '';
        },
    },

    getters: {
        productMap: state => state.products,
    },
});
